import React from 'react';
import { Shield, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
    return (
        <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
            <div className="max-w-4xl mx-auto px-6 py-12">
                <Link to="/" className="inline-flex items-center text-slate-500 hover:text-purple-600 mb-8 transition-colors">
                    <ArrowLeft size={20} className="mr-2" /> Tillbaka till startsidan
                </Link>

                <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 md:p-12">
                    <div className="flex items-center mb-8">
                        <div className="p-3 bg-purple-100 rounded-xl mr-4">
                            <Shield className="w-8 h-8 text-purple-600" />
                        </div>
                        <h1 className="text-3xl md:text-4xl font-bold text-slate-900">Integritetspolicy</h1>
                    </div>

                    <div className="prose prose-slate max-w-none">
                        <p className="lead text-lg text-slate-600 mb-8">
                            Din integritet är kärnan i vår verksamhet. Denna policy beskriver hur Noctra, en tjänst som drivs av Lumidiom AB (org.nr 559569-4737), samlar in, använder och skyddar dina personuppgifter.
                        </p>

                        <h3>1. Personuppgiftsansvarig</h3>
                        <p>
                            <strong>Lumidiom AB</strong> (org.nr 559569-4737), som driver tjänsten under varumärket Noctra, är personuppgiftsansvarig för den behandling av personuppgifter som sker genom tjänsten.
                        </p>
                        <p>
                            Kontakt: <a href="mailto:support@noctra.se">support@noctra.se</a>
                        </p>

                        <h3>2. Vilka uppgifter samlar vi in?</h3>
                        <p>För att kunna leverera vår tjänst behandlar vi följande kategorier av personuppgifter:</p>
                        <ul>
                            <li><strong>Identitetsuppgifter:</strong> Namn och personnummer (för säker identifiering mot databaser och söktjänster).</li>
                            <li><strong>Kontaktuppgifter:</strong> E-postadress, telefonnummer och folkbokföringsadress.</li>
                            <li><strong>Betalningsinformation:</strong> Hanteras säkert via vår betalningspartner Stripe. Vi lagrar aldrig dina kortuppgifter.</li>
                            <li><strong>Ärendeinformation:</strong> Status på borttagningsärenden hos söktjänster (t.ex. Ratsit, Eniro, MrKoll, Hitta.se).</li>
                            <li><strong>Teknisk data:</strong> IP-adress, webbläsartyp och sidinteraktioner via cookies (se avsnitt 8).</li>
                        </ul>

                        <h3>3. Rättslig grund för behandling (GDPR art. 6)</h3>
                        <p>Vi behandlar dina uppgifter med stöd av följande rättsliga grunder:</p>
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="bg-slate-50">
                                    <th className="border border-slate-200 p-3 text-left font-semibold">Ändamål</th>
                                    <th className="border border-slate-200 p-3 text-left font-semibold">Rättslig grund</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="border border-slate-200 p-3">Leverans av tjänst (borttagning av uppgifter)</td>
                                    <td className="border border-slate-200 p-3"><strong>Fullgörande av avtal</strong> (art. 6.1 b)</td>
                                </tr>
                                <tr>
                                    <td className="border border-slate-200 p-3">Betalningshantering</td>
                                    <td className="border border-slate-200 p-3"><strong>Fullgörande av avtal</strong> (art. 6.1 b)</td>
                                </tr>
                                <tr>
                                    <td className="border border-slate-200 p-3">Statusuppdateringar och kommunikation</td>
                                    <td className="border border-slate-200 p-3"><strong>Berättigat intresse</strong> (art. 6.1 f)</td>
                                </tr>
                                <tr>
                                    <td className="border border-slate-200 p-3">Bokföring och lagkrav</td>
                                    <td className="border border-slate-200 p-3"><strong>Rättslig förpliktelse</strong> (art. 6.1 c)</td>
                                </tr>
                                <tr>
                                    <td className="border border-slate-200 p-3">Analys och förbättring av tjänsten</td>
                                    <td className="border border-slate-200 p-3"><strong>Samtycke</strong> (art. 6.1 a) — via cookie-samtycke</td>
                                </tr>
                            </tbody>
                        </table>

                        <h3>4. Fullmakt och behandling hos tredje part</h3>
                        <p>
                            Genom att använda tjänsten ger du oss fullmakt att agera i ditt namn gentemot databaser, sökmotorer och upplysningssajter. Vi delar <strong>enbart de uppgifter som krävs</strong> (namn och personnummer) med dessa parter i det uttryckliga syftet att begära radering eller blockering av dina uppgifter.
                        </p>

                        <h3>5. Tredjepartstjänster vi använder</h3>
                        <p>Vi använder följande underleverantörer som kan komma att behandla dina personuppgifter:</p>
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="bg-slate-50">
                                    <th className="border border-slate-200 p-3 text-left font-semibold">Tjänst</th>
                                    <th className="border border-slate-200 p-3 text-left font-semibold">Syfte</th>
                                    <th className="border border-slate-200 p-3 text-left font-semibold">Land</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td className="border border-slate-200 p-3">Stripe</td>
                                    <td className="border border-slate-200 p-3">Betalningshantering</td>
                                    <td className="border border-slate-200 p-3">USA (EU SCC)</td>
                                </tr>
                                <tr>
                                    <td className="border border-slate-200 p-3">Supabase</td>
                                    <td className="border border-slate-200 p-3">Databas och autentisering</td>
                                    <td className="border border-slate-200 p-3">EU (Frankfurt)</td>
                                </tr>
                                <tr>
                                    <td className="border border-slate-200 p-3">Resend</td>
                                    <td className="border border-slate-200 p-3">Transaktionsmail</td>
                                    <td className="border border-slate-200 p-3">USA (EU SCC)</td>
                                </tr>

                                <tr>
                                    <td className="border border-slate-200 p-3">Google Analytics</td>
                                    <td className="border border-slate-200 p-3">Webbanalys (anonymiserat)</td>
                                    <td className="border border-slate-200 p-3">USA (EU SCC)</td>
                                </tr>
                            </tbody>
                        </table>
                        <p className="text-sm text-slate-500 mt-2">
                            "EU SCC" innebär att leverantören följer EU:s standardavtalsklausuler för överföring till tredje land.
                        </p>

                        <h3>6. Lagringstider</h3>
                        <p>Vi sparar dina uppgifter enligt följande:</p>
                        <ul>
                            <li><strong>Kontouppgifter:</strong> Så länge du har ett aktivt konto hos oss, plus 30 dagar efter avslut.</li>
                            <li><strong>Personnummer:</strong> Raderas inom 30 dagar efter att ditt konto avslutas eller tjänsten slutförts.</li>
                            <li><strong>Betalningshistorik:</strong> 7 år enligt bokföringslagen (1999:1078).</li>
                            <li><strong>Ärendeloggar:</strong> 12 månader efter avslutat ärende, som bevis för utfört arbete.</li>
                            <li><strong>Cookie-data:</strong> Enligt våra cookie-inställningar (se avsnitt 8).</li>
                        </ul>

                        <h3>7. Säkerhet</h3>
                        <p>
                            Vi skyddar dina uppgifter med branschstandardiserade säkerhetsåtgärder. All kommunikation sker krypterat via SSL/TLS. Känsliga uppgifter som personnummer hanteras med strikt behörighetsstyrning och lagras i krypterad databas.
                        </p>

                        <h3>8. Cookies</h3>
                        <p>
                            Vi använder cookies för att förbättra din upplevelse, hålla dig inloggad och analysera besöksstatistik. Vid ditt första besök kan du välja vilka cookies du godkänner. Du kan när som helst ändra dina inställningar i webbläsaren.
                        </p>
                        <ul>
                            <li><strong>Nödvändiga cookies:</strong> Krävs för inloggning och grundläggande funktionalitet (kan ej stängas av).</li>
                            <li><strong>Analyscookies:</strong> Hjälper oss förstå hur tjänsten används (kräver samtycke).</li>
                        </ul>

                        <h3>9. Dina rättigheter</h3>
                        <p>Enligt GDPR har du rätt att:</p>
                        <ul>
                            <li><strong>Tillgång:</strong> Begära utdrag över vilka uppgifter vi behandlar om dig.</li>
                            <li><strong>Rättelse:</strong> Begära att felaktiga uppgifter korrigeras.</li>
                            <li><strong>Radering:</strong> Begära att dina uppgifter raderas ("rätten att bli bortglömd"), om det inte krävs för att fullgöra avtal eller lagliga skyldigheter.</li>
                            <li><strong>Begränsning:</strong> Begära att behandlingen begränsas under tiden en tvist utreds.</li>
                            <li><strong>Dataportabilitet:</strong> Få dina uppgifter i ett maskinläsbart format.</li>
                            <li><strong>Invändning:</strong> Invända mot behandling som baseras på berättigat intresse.</li>
                            <li><strong>Återkalla samtycke:</strong> Om behandlingen baseras på samtycke kan du när som helst återkalla det.</li>
                        </ul>
                        <p>
                            Kontakta oss på <a href="mailto:support@noctra.se" className="text-purple-600 hover:underline">support@noctra.se</a> för att utöva dina rättigheter. Vi svarar inom 30 dagar.
                        </p>

                        <h3>10. Klagomål</h3>
                        <p>
                            Om du anser att vi behandlar dina personuppgifter felaktigt har du rätt att lämna klagomål till <strong>Integritetsskyddsmyndigheten (IMY)</strong>.
                        </p>
                        <p className="text-sm text-slate-500">
                            Integritetsskyddsmyndigheten<br />
                            Box 8114, 104 20 Stockholm<br />
                            <a href="https://www.imy.se" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:underline">www.imy.se</a>
                        </p>

                        <h3>11. Ändringar i policyn</h3>
                        <p>
                            Vi kan komma att uppdatera denna policy. Den senaste versionen finns alltid tillgänglig på vår hemsida. Vid väsentliga ändringar meddelar vi dig via e-post.
                        </p>

                        <div className="mt-12 pt-8 border-t border-slate-200 text-sm text-slate-500">
                            <p>Senast uppdaterad: 2026-02-26</p>
                            <p>Lumidiom AB (org.nr 559569-4737), driver tjänsten Noctra</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PrivacyPolicy;
